import { useEffect, useRef, useState } from 'react'

import { ConnectorHealth } from '@/constants/medconnect-devices-types'
import { ConnectorUnreachableError, probeConnectorHealth } from '@/core/classes/MedconnectDevicesAPI'

export type ConnectorHealthState = {
  health: ConnectorHealth | null
  /** True once the first request has settled, whatever its outcome. */
  isSettled: boolean
  /** True when the connector could not be reached at all. */
  isUnreachable: boolean
  /**
   * True when a connector answers but cannot provide a supported health report.
   * `health` is then `null`; guided setup also checks the device catalogue.
   */
  isLegacy: boolean
}

const INITIAL_STATE: ConnectorHealthState = { health: null, isSettled: false, isUnreachable: false, isLegacy: false }

/** Poll the local connector's health endpoint; `pollMs = 0` fetches once. */
export function useConnectorHealth(pollMs = 3000): ConnectorHealthState {
  const [state, setState] = useState<ConnectorHealthState>(INITIAL_STATE)

  useEffect(() => {
    let cancelled = false

    const refresh = async () => {
      const next = await resolveConnectorHealth()
      if (!cancelled) setState(next)
    }

    void refresh()
    if (pollMs <= 0) return () => { cancelled = true }
    const timer = setInterval(() => void refresh(), pollMs)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [pollMs])

  return state
}

/**
 * Whether `next` shows the connector came up, came back, or restarted since `previous`: a lower uptime, or a
 * switch between an old and a current build, means another process (a reinstall, an upgrade) now answers.
 */
export function hasConnectorRestarted(previous: ConnectorHealthState, next: ConnectorHealthState): boolean {
  const isUp = next.isSettled && !next.isUnreachable
  if (!isUp) return false
  if (!previous.isSettled || previous.isUnreachable) return true
  if (previous.isLegacy !== next.isLegacy) return true
  return !!previous.health && !!next.health && next.health.uptimeSeconds < previous.health.uptimeSeconds
}

/**
 * Counter bumped each time the connector comes up or restarts (0 until it first answers). Data the connector
 * serves once (device catalogue, version) must be reloaded on each bump, or an install done with the page open
 * leaves it stale — e.g. an empty catalogue read while the service was restarting shows "outdated" for good.
 */
export function useConnectorGeneration(health: ConnectorHealthState): number {
  const previous = useRef<ConnectorHealthState>(INITIAL_STATE)
  const [generation, setGeneration] = useState(0)

  useEffect(() => {
    if (hasConnectorRestarted(previous.current, health)) setGeneration((value) => value + 1)
    previous.current = health
  }, [health])

  return generation
}

/** One settled health state from a single probe; never throws. */
export async function resolveConnectorHealth(): Promise<ConnectorHealthState> {
  try {
    const health = await probeConnectorHealth()
    return { health, isSettled: true, isUnreachable: false, isLegacy: health === null }
  } catch (error) {
    // Any failure other than a deliberate "nothing answers" is treated the same way: the user can't use the connector.
    if (!(error instanceof ConnectorUnreachableError)) console.error(error)
    return { health: null, isSettled: true, isUnreachable: true, isLegacy: false }
  }
}

export type ConnectorDeviceLiveStatus = 'connected' | 'waiting' | 'unknown'

/** Identifies a configured device in the health report: by entry id when known, else by configured name. */
export type ConfiguredDeviceRef = { configId?: string; configName: string }

/** Live status of one configured device, from the connector's health report. */
export function getDeviceLiveStatus(health: ConnectorHealth | null, ref: ConfiguredDeviceRef): ConnectorDeviceLiveStatus {
  const entry = health?.devices?.find((device) => (ref.configId ? device.configId === ref.configId : device.configName === ref.configName || device.name === ref.configName))
  if (!entry) return 'unknown'
  if (entry.connected === null) return entry.alive ? 'connected' : 'unknown'
  return entry.connected || entry.alive ? 'connected' : 'waiting'
}
